import React from "react";
import { FlatList, Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import moment from "moment";

import { kRoute } from "src/kRoute";
import * as orderDb from "src/db/order.db";
import { PdBadge, PdBadgeLabel, PdText } from "src/components/layout";
import {
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/layout/table";
import { tPressableState } from "src/components/types";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useIsMobile, useUserProfile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { formatMoney } from "src/util/gFormatting";
import { getThemeColorFromOrderStatus } from "src/util/ui";

import * as mappers from "tmt_api/src/shared/mappers";
import { tOrderStatus, tUserAccountType } from "tmt_api/src/types/types.validator";

type tTableOrder = {
  orderId: string;
  orderDate: number;
  brandName: string;
  retailerName: string;
  source: string;
  status: tOrderStatus;
  total: number;
};

const formatOrderMobileDate = (dateInMilliseconds: number) => moment(dateInMilliseconds).format("MMM DD, YYYY");

const formatOrderDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const OrderMobileRow: React.FC<{
  item: tRowInstance<tTableOrder>;
  onPress?: (item: tRowInstance<tTableOrder>) => void;
}> = ({ item, onPress }) => {
  const { isBrand } = useUserProfile();

  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <TouchableOpacity style={styles.orderMobileRow} onPress={handleOnPress}>
      <View style={styles.flex1}>
        <PdText>{item.retailerName}</PdText>
        {!isBrand && <PdText>{item.brandName}</PdText>}
        <PdText>{formatOrderMobileDate(item.orderDate)}</PdText>
        <PdText>{formatMoney(item.total)}</PdText>
      </View>
      <PdBadge
        style={[styles.orderMobileRowContainerBadge, { backgroundColor: getThemeColorFromOrderStatus(item.status) }]}
      >
        <PdBadgeLabel>{item.status}</PdBadgeLabel>
      </PdBadge>
    </TouchableOpacity>
  );
};

const OrderRow: React.FC<{ item: tRowInstance<tTableOrder>; onPress?: (item: tRowInstance<tTableOrder>) => void }> = ({
  item,
  onPress,
}) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <Pressable onPress={handleOnPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow style={[hovered ? styles.orderRowHovered : {}, pressed ? styles.orderRowPressed : {}]}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};

const RecentOrdersTable: React.FC<{ type: tUserAccountType; companyId: string }> = ({ type, companyId }) => {
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const [data, setData] = React.useState<tTableOrder[]>([]);
  const tableInstance = useTable<tTableOrder>({
    data,
    columns,
    initialState: {
      sortedBy: "orderDate",
      sortDirection: "desc",
    },
  });

  const navigateToOrderView = React.useCallback(
    (row: tRowInstance<tTableOrder>) => {
      navigation.navigate(kRoute.P03_01, { id: row.orderId });
    },
    [navigation],
  );

  const renderOrderItem: React.FC<{ item: tRowInstance<tTableOrder> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <OrderMobileRow item={item} onPress={navigateToOrderView} />;
      return <OrderRow item={item} onPress={navigateToOrderView} />;
    },
    [isMobile, navigateToOrderView],
  );

  React.useEffect(() => {
    (async () => {
      const params: orderDb.tQueryOrdersParams = {};
      if (type === "brand") params.brandId = companyId;
      if (type === "rep") params.repId = companyId;
      if (type === "retailer") params.retailerId = companyId;
      const ordersQuery = await orderDb.queryOrder(params).limit(5);

      const unsubscribe = ordersQuery.onSnapshot((snapshot) => {
        setData(snapshot.docs.map((doc) => mappers.mapOrder(doc.data(), type)));
      });

      return () => unsubscribe();
    })();
  }, [companyId, type]);

  return (
    <FlatList
      ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
      ListEmptyComponent={PdTableEmpty}
      data={tableInstance.rows}
      keyExtractor={(item) => item.orderId}
      renderItem={renderOrderItem}
      key={`${isMobile}`}
    />
  );
};

const styles = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  orderMobileRow: {
    alignItems: "center",
    flexDirection: "row",
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  orderMobileRowContainerBadge: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  orderRowHovered: {
    backgroundColor: kColors.gray50,
  },
  orderRowPressed: {
    opacity: 0.6,
  },
});

const columns: tColumn<tTableOrder>[] = [
  {
    id: "orderDate",
    header: "Order Date",
    accessor: (originalRow) => formatOrderDate(originalRow.orderDate),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "retailerName",
    header: "Retailer Name",
    accessor: (originalRow) => originalRow.retailerName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "source",
    header: "Source",
    accessor: (originalRow) => originalRow.source,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "status",
    header: "Order Status",
    accessor: (originalRow) => originalRow.status,
    Cell: ({ value, row }) => (
      <PdBadge style={{ backgroundColor: getThemeColorFromOrderStatus(row.status) }}>
        <PdBadgeLabel>{value}</PdBadgeLabel>
      </PdBadge>
    ),
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "total",
    header: "Order Total",
    accessor: (originalRow) => formatMoney(originalRow.total),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
];

export default RecentOrdersTable;
